import axios, { AxiosInstance } from 'axios';
import { getApiHost, getServiceToken } from '@/shared/lib/utils/env';
import './types'; // load InternalAxiosRequestConfig augmentation (meta)
import { handleAxiosError, handleUnknownError } from './errorHandler';

/**
 * Convert camelCase to kebab-case
 */
const toKebabCase = (str: string): string => str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * Transform object keys from camelCase to kebab-case
 */
const transformParamsToKebab = (params: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(params)) {
    result[toKebabCase(key)] = params[key];
  }
  return result;
};

/**
 * Configuration for creating an API client
 */
interface ApiClientConfig {
  baseURL: string;
}

/**
 * Create an API client with the given configuration
 * @param config - Configuration for the API client
 * @returns An axios instance configured for the API
 */
export const createApiClient = (config: ApiClientConfig): AxiosInstance => {
  // Otherwise, create a real axios instance
  const instance = axios.create({
    baseURL: config.baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  // Add a request interceptor: dynamic baseURL from getApiHost() and authorization header
  instance.interceptors.request.use(
    (config) => {
      config.baseURL = getApiHost() || '/';

      // Подставляем сервисный токен, если он задан
      const serviceToken = getServiceToken();
      if (serviceToken) {
        config.headers['Authorization'] = `Bearer ${serviceToken}`;
      }

      // Имена query-параметров приводятся к kebab-case,
      // кроме запросов, чей контракт задаёт их как есть (preserveParamsCase)
      if (config.params && typeof config.params === 'object' && !config.preserveParamsCase) {
        config.params = transformParamsToKebab(config.params);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Add a response interceptor to attach meta on config and handle errors
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: unknown) => {
      // Используем axios для проверки типа ошибки
      if (axios.isAxiosError(error)) {
        return handleAxiosError(error);
      }

      return handleUnknownError(error);
    },
  );

  return instance;
};

/**
 * Default API client for the application
 * Uses mock data in development and real API in production
 */
export const apiClient = createApiClient({
  baseURL: getApiHost() || '/',
});
